/**
 * Address formatting helpers — single source of truth for derived address
 * fields on the client. Mirrors `votemate-backend/utils/address_format.py`.
 *
 * We persist only `address_components` on `user_pii`; the formatted display
 * string is derived on demand via `formatAddress(components)`.
 */

export interface AddressComponents {
  street?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  county?: string;
  // Allow extra keys without TypeScript complaining for forward compatibility.
  [key: string]: unknown;
}

/**
 * Render a one-line postal address string from components. Returns an empty
 * string when components are missing or incomplete enough to be useful (no
 * street and no city).
 *
 * Format mirrors Google Places `formatted_address`:
 * `"<street>, <city>, <state> <zip>, USA"`.
 */
export function formatAddress(components: AddressComponents | null | undefined): string {
  if (!components) return '';

  const street = String(components.street ?? '').trim();
  const city = String(components.city ?? '').trim();
  const state = String(components.state ?? '').trim();
  const zip = String(components.zip_code ?? '').trim();

  if (!street && !city) return '';

  const parts: string[] = [];
  if (street) parts.push(street);
  if (city) parts.push(city);
  const stateZip = [state, zip].filter(Boolean).join(' ');
  if (stateZip) parts.push(stateZip);
  parts.push('USA');
  return parts.join(', ');
}
